import { useState } from 'react';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  requireInput?: boolean;
  inputPlaceholder?: string;
  expectedInput?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmModal({
  title,
  message,
  confirmText = '确认',
  cancelText = '取消',
  type = 'warning',
  requireInput = false,
  inputPlaceholder = '',
  expectedInput = '',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const [inputValue, setInputValue] = useState('');

  const getTypeConfig = () => {
    const configs = {
      danger: {
        icon: 'ri-error-warning-line',
        iconColor: 'text-red-600',
        bgColor: 'bg-red-50',
        buttonColor: 'bg-red-600 hover:bg-red-700',
      },
      warning: {
        icon: 'ri-alert-line',
        iconColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
      },
      info: {
        icon: 'ri-information-line',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        buttonColor: 'bg-blue-600 hover:bg-blue-700',
      },
    };
    return configs[type];
  };

  const config = getTypeConfig();
  const canConfirm = !requireInput || inputValue === expectedInput;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
              <i className={`${config.icon} text-2xl ${config.iconColor}`}></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">{message}</p>
              {requireInput && (
                <div className="mt-4">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={inputPlaceholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {inputValue && !canConfirm && (
                    <p className="text-xs text-red-600 mt-1">输入内容不匹配</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            className={`px-4 py-2 text-white rounded-lg text-sm transition-colors whitespace-nowrap ${
              canConfirm ? config.buttonColor : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
